import React, { useRef, useEffect } from 'react';

interface AudioVisualizerProps {
  audioLevel: number;
  isActive: boolean;
}

export default function AudioVisualizer({ audioLevel, isActive }: AudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.fillStyle = '#1a202c';
    ctx.fillRect(0, 0, width, height);

    if (isActive) {
      // Draw single bar representing actual audio level (NO random variation)
      const barWidth = width * 0.8;
      const barX = (width - barWidth) / 2;
      const maxBarHeight = height * 0.8;
      const barHeight = (audioLevel / 100) * maxBarHeight;

      // Color gradient based on audio level
      const hue = Math.max(0, 120 - audioLevel * 1.2); // Green to red
      ctx.fillStyle = `hsl(${hue}, 70%, 50%)`;

      const y = (height - barHeight) / 2;
      ctx.fillRect(barX, y, barWidth, barHeight);

      // Draw level indicator text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${audioLevel.toFixed(1)}%`, width / 2, height / 2);
    } else {
      // Show inactive state
      ctx.fillStyle = '#4a5568';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No Audio Signal', width / 2, height / 2);
    }
  }, [audioLevel, isActive]);

  return (
    <div style={styles.container}>
      <canvas
        ref={canvasRef}
        width={800}
        height={200}
        style={styles.canvas}
      />
      <div style={styles.levelIndicator}>
        <span style={styles.label}>Audio Level:</span>
        <div style={styles.levelBar}>
          <div
            style={{
              ...styles.levelFill,
              width: `${audioLevel}%`,
              backgroundColor: getAudioLevelColor(audioLevel),
            }}
          />
        </div>
        <span style={styles.value}>{audioLevel.toFixed(0)}%</span>
      </div>
    </div>
  );
}

function getAudioLevelColor(level: number): string {
  if (level < 20) return '#48bb78'; // Green
  if (level < 50) return '#38b2ac'; // Teal
  if (level < 70) return '#ed8936'; // Orange
  return '#f56565'; // Red
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  canvas: {
    width: '100%',
    height: 'auto',
    borderRadius: '8px',
    border: '2px solid #2d3748',
  },
  levelIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2d3748',
    minWidth: '90px',
  },
  levelBar: {
    flex: 1,
    height: '24px',
    backgroundColor: '#e2e8f0',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #cbd5e0',
  },
  levelFill: {
    height: '100%',
    transition: 'width 0.1s ease-out, background-color 0.3s',
  },
  value: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2d3748',
    minWidth: '45px',
    textAlign: 'right',
  },
};
